// Le fichier clients.

import { useCallback, useState } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import Table from '../../ui/Table.jsx';
import Button from '../../ui/Button.jsx';
import Badge from '../../ui/Badge.jsx';
import Modal from '../../ui/Modal.jsx';
import { TextField } from '../../ui/TextField.jsx';
import NumberField from '../../ui/NumberField.jsx';
import { EmptyState, ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useToast } from '../../ui/Toast.jsx';
import { useResource } from '../../lib/useResource.js';
import { api } from '../../lib/api.js';

const EMPTY = {
  company: '', contact: '', email: '', phone: '', client_number: '',
  address: '', postal_code: '', city: '', vat: '', discount_pct: null,
  payment_terms: '',
};

export function ClientsPage() {
  const toast = useToast();
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);

  const load = useCallback(
    () => api.get(`/clients?per_page=100${q ? `&q=${encodeURIComponent(q)}` : ''}`),
    [q],
  );
  const { data, error, loading, reload } = useResource(load, [q]);

  const columns = [
    { key: 'company', label: 'Société' },
    { key: 'contact', label: 'Contact', render: (c) => c.contact || '—' },
    { key: 'city', label: 'Ville', render: (c) => c.city || '—' },
    { key: 'email', label: 'E-mail', render: (c) => c.email || '—' },
    { key: 'devis_count', label: 'Devis', numeric: true },
    {
      key: 'active',
      label: 'État',
      render: (c) => (c.active ? null : <Badge tone="neutral">Désactivé</Badge>),
    },
    {
      key: 'actions',
      label: '',
      render: (c) => (
        <Button size="sm" variant="ghost" onClick={() => setEditing(c)}>
          Modifier
        </Button>
      ),
    },
  ];

  return (
    <>
      <Card>
        <CardHead title="Clients">
          <Button variant="primary" size="sm" onClick={() => setEditing(EMPTY)}>
            Nouveau client
          </Button>
        </CardHead>
        <CardBody className="stack">
          <TextField
            label="Rechercher"
            placeholder="Société, contact, numéro client, ville…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {loading ? (
            <SkeletonRows rows={4} />
          ) : error ? (
            <ErrorState error={error} onRetry={reload} />
          ) : (
            <Table
              columns={columns}
              rows={data.rows}
              caption="Fichier clients"
              empty={
                <EmptyState title={q ? 'Aucun client ne correspond' : 'Aucun client'}>
                  {q ? 'Modifiez la recherche.' : 'Créez le premier client pour pouvoir chiffrer.'}
                </EmptyState>
              }
            />
          )}
        </CardBody>
      </Card>

      {editing ? (
        <ClientForm
          client={editing}
          onClose={() => setEditing(null)}
          onSaved={(message) => {
            toast.success(message);
            setEditing(null);
            reload();
          }}
          onError={(err) => toast.error(err.message)}
        />
      ) : null}
    </>
  );
}

function ClientForm({ client, onClose, onSaved, onError }) {
  const [form, setForm] = useState(client);
  const [errors, setErrors] = useState({});
  const [pending, setPending] = useState(false);
  const isNew = !client.id;

  const set = (patch) => setForm({ ...form, ...patch });

  async function submit(event) {
    event.preventDefault();
    setPending(true);
    setErrors({});
    const body = {
      company: form.company, contact: form.contact, email: form.email,
      phone: form.phone, client_number: form.client_number, address: form.address,
      postal_code: form.postal_code, city: form.city, vat: form.vat,
      discount_pct: form.discount_pct, payment_terms: form.payment_terms,
    };
    try {
      if (isNew) await api.post('/clients', body);
      else await api.patch(`/clients/${client.id}`, body);
      onSaved(isNew ? 'Client créé.' : 'Client enregistré.');
    } catch (err) {
      if (err.details) setErrors(err.details);
      else onError(err);
    } finally {
      setPending(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isNew ? 'Nouveau client' : form.company}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button variant="primary" type="submit" form="form-client" disabled={pending}>
            {pending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </>
      }
    >
      <form id="form-client" className="stack" onSubmit={submit}>
        <TextField label="Société" required value={form.company ?? ''} error={errors.company}
          onChange={(e) => set({ company: e.target.value })} />
        <div className="grid-2">
          <TextField label="Contact" value={form.contact ?? ''} error={errors.contact}
            onChange={(e) => set({ contact: e.target.value })} />
          <TextField label="E-mail" type="email" value={form.email ?? ''} error={errors.email}
            onChange={(e) => set({ email: e.target.value })} />
          <TextField label="Téléphone" value={form.phone ?? ''} error={errors.phone}
            onChange={(e) => set({ phone: e.target.value })} />
          <TextField label="Numéro client" value={form.client_number ?? ''}
            error={errors.client_number}
            onChange={(e) => set({ client_number: e.target.value })} />
        </div>
        <TextField label="Adresse" value={form.address ?? ''} error={errors.address}
          onChange={(e) => set({ address: e.target.value })} />
        <div className="grid-2">
          <TextField label="Code postal" value={form.postal_code ?? ''} error={errors.postal_code}
            onChange={(e) => set({ postal_code: e.target.value })} />
          <TextField label="Ville" value={form.city ?? ''} error={errors.city}
            onChange={(e) => set({ city: e.target.value })} />
          <TextField label="TVA" value={form.vat ?? ''} error={errors.vat}
            hint="Intracommunautaire : article 39bis sur le devis."
            onChange={(e) => set({ vat: e.target.value })} />
          <NumberField label="Remise" unit="%" value={form.discount_pct} min={0} max={100}
            error={errors.discount_pct}
            onChange={(v) => set({ discount_pct: v })} />
        </div>
        <TextField label="Conditions de paiement" value={form.payment_terms ?? ''}
          error={errors.payment_terms}
          onChange={(e) => set({ payment_terms: e.target.value })} />
      </form>
    </Modal>
  );
}

export default ClientsPage;
